import {
  Activity,
  AlertTriangle,
  AppWindow,
  ArrowUpRight,
  Bot,
  Boxes,
  CalendarClock,
  Check,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  Copy,
  Eye,
  EyeOff,
  FileImage,
  Gauge,
  KeyRound,
  LayoutDashboard,
  LayoutGrid,
  LayoutList,
  LockKeyhole,
  LogOut,
  Menu,
  PauseCircle,
  PlayCircle,
  Plus,
  Power,
  RefreshCw,
  Route as RouteIcon,
  RadioTower,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Trash2,
  TrendingUp,
  TriangleAlert,
  User,
  X,
} from 'lucide-react';
import { FormEvent, ReactNode, useEffect, useState } from 'react';
import { AdminApiError, adminRequest } from './api';
import { Operations } from './Operations';
import { ModelModal } from './ModelModal';
import { ToastContainer, showToast } from './Toast';
import { ProviderBadge, ProviderIcon } from './ProviderIcon';
import { AnalyticsTab } from './AnalyticsTab';
import type { AiRequest, ApiKey, AppConfiguration, Application, Model, Overview, Purpose, PurposeRegistry, Quota, Route } from './types';

type Section = 'overview' | 'applications' | 'models' | 'operations' | 'requests';
type Modal = 'application' | 'model' | 'key' | 'route' | 'quota' | null;

const EMPTY_OVERVIEW: Overview = {
  totals: { applications: 0, requests: 0, completed: 0, failed: 0, inputTokens: 0, outputTokens: 0, providerCostUsdMicro: 0, billedCredits: 0, averageLatencyMs: 0 },
  series: [], byApp: [], byModel: [], byFeature: [],
};

const ALL_SCOPES = [
  'responses:create', 'responses:read', 'files:write', 'files:delete',
  'jobs:create', 'jobs:read', 'jobs:cancel', 'installations:write', 'installations:delete',
  'purposes:sync',
];

function compact(value: number): string {
  return new Intl.NumberFormat('en', { notation: value >= 10_000 ? 'compact' : 'standard', maximumFractionDigits: 1 }).format(value);
}

function money(micro: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: micro >= 1_000_000 ? 2 : 4, maximumFractionDigits: 4 }).format(micro / 1_000_000);
}

function dateTime(value: string | null): string {
  if (!value) return 'Never';
  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}

function classNames(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}

function StatusPill({ status }: { status: string }) {
  return <span className={`status status--${status}`}><span />{status}</span>;
}

function SectionTitle({ eyebrow, title, detail, action }: { eyebrow: string; title: string; detail: string; action?: ReactNode }) {
  return (
    <div className="section-title">
      <div><p className="eyebrow">{eyebrow}</p><h2>{title}</h2><p>{detail}</p></div>
      {action}
    </div>
  );
}

function EmptyState({ icon, title, detail }: { icon: ReactNode; title: string; detail: string }) {
  return <div className="empty-state"><span>{icon}</span><strong>{title}</strong><p>{detail}</p></div>;
}

function Login({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true); setError('');
    try {
      await adminRequest('/session');
      onAuthenticated();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Authentication failed.');
    } finally { setBusy(false); }
  }

  return (
    <main className="login-shell">
      <div className="login-atmosphere login-atmosphere--one" />
      <div className="login-atmosphere login-atmosphere--two" />
      <section className="login-story">
        <div className="brand-mark"><Boxes size={24} /><span>relay</span></div>
        <p className="eyebrow">One AI control plane</p>
        <h1>Every app.<br /><em>One intelligent layer.</em></h1>
        <p className="login-copy">Route models, enforce budgets, and understand AI usage across every product from a single edge-native platform.</p>
        <div className="trust-row">
          <span><ShieldCheck size={17} /> App-isolated</span>
          <span><Gauge size={17} /> Cost-aware</span>
          <span><FileImage size={17} /> Vision-ready</span>
        </div>
      </section>
      <section className="login-card-wrap">
        <form className="login-card" onSubmit={submit}>
          <div className="login-lock"><LockKeyhole size={23} /></div>
          <p className="eyebrow">Operations console</p>
          <h2>Authenticate to continue</h2>
          <p>Access is controlled by your organization through Cloudflare Access and the platform role registry.</p>
          {error && <div className="form-error"><TriangleAlert size={16} />{error}</div>}
          <button className="button button--primary button--wide" disabled={busy}>{busy ? <RefreshCw className="spin" size={17} /> : <ArrowUpRight size={17} />}{busy ? 'Checking access' : 'Continue with Cloudflare Access'}</button>
          <small>No platform credential is stored in the browser.</small>
        </form>
      </section>
    </main>
  );
}

function UsageChart({ overview }: { overview: Overview }) {
  const width = 760; const height = 230; const top = 18; const bottom = 28;
  const values = overview.series.map((point) => point.requests);
  const max = Math.max(...values, 1);
  const points = overview.series.map((point, index) => ({
    x: overview.series.length === 1 ? width / 2 : index * (width / (overview.series.length - 1)),
    y: top + (1 - point.requests / max) * (height - top - bottom),
  }));
  const line = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
  const area = points.length ? `${line} L ${points.at(-1)!.x} ${height - bottom} L ${points[0]!.x} ${height - bottom} Z` : '';
  return (
    <div className="chart-wrap">
      {points.length === 0 ? <EmptyState icon={<Activity />} title="No usage in this window" detail="Requests will appear here after an application begins using the API." /> : (
        <svg className="chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Requests over time">
          <defs><linearGradient id="area" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#df6b4f" stopOpacity=".26" /><stop offset="1" stopColor="#df6b4f" stopOpacity="0" /></linearGradient></defs>
          {[0, 1, 2, 3].map((row) => <line key={row} x1="0" x2={width} y1={top + row * ((height - top - bottom) / 3)} y2={top + row * ((height - top - bottom) / 3)} className="grid-line" />)}
          <path d={area} fill="url(#area)" /><path d={line} className="chart-line" />
          {points.map((point, index) => <circle key={overview.series[index]!.day} cx={point.x} cy={point.y} r="3.5" className="chart-dot"><title>{overview.series[index]!.day}: {overview.series[index]!.requests} requests</title></circle>)}
        </svg>
      )}
    </div>
  );
}

function ModalShell({ title, detail, onClose, children }: { title: string; detail: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="modal" role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <button className="icon-button modal-close" onClick={onClose} aria-label="Close"><X size={19} /></button>
        <p className="eyebrow">Configure platform</p><h2 id="modal-title">{title}</h2><p className="modal-detail">{detail}</p>
        {children}
      </section>
    </div>
  );
}

function supportsPurpose(model: Model, purpose: Purpose | undefined): boolean {
  if (!purpose) return true;
  return purpose.requiredCapabilities.every((capability) => (
    model.capabilities.includes(capability)
    && (capability !== 'structured_output' || model.supportsStrictSchema === 1)
  ));
}

function RouteModal({
  purposeKey,
  purposes,
  models,
  busy,
  onClose,
  onSubmit,
}: {
  purposeKey: string;
  purposes: Purpose[];
  models: Model[];
  busy: boolean;
  onClose: () => void;
  onSubmit: (input: {
    feature: string;
    modelId: string;
    enabled: boolean;
    timeoutMs: number;
    maxOutputTokens: number | null;
    temperature: number;
  }) => void;
}) {
  const [selection, setSelection] = useState(purposeKey);
  const selectedPurpose = purposes.find((purpose) => purpose.key === selection);
  const manual = selection === '__manual__';
  const compatibleModels = models.filter((model) => (
    model.status === 'active' && supportsPurpose(model, selectedPurpose)
  ));

  return (
    <ModalShell
      title="Configure model route"
      detail="Assign a registered purpose to a verified model inside this application boundary."
      onClose={onClose}
    >
      <form className="form-grid" onSubmit={(event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        onSubmit({
          feature: manual ? String(data.get('manualFeature') || '') : selection,
          modelId: String(data.get('modelId') || ''),
          enabled: true,
          timeoutMs: Number(data.get('timeout')),
          maxOutputTokens: Number(data.get('maxOutput')) || null,
          temperature: Number(data.get('temperature')),
        });
      }}>
        <label className="span-2">
          Purpose
          <select value={selection} onChange={(event) => setSelection(event.target.value)} required>
            <option value="">Select registered purpose</option>
            {purposes.filter((purpose) => purpose.status === 'active').map((purpose) => (
              <option key={purpose.id} value={purpose.key}>{purpose.name} ({purpose.key})</option>
            ))}
            <option value="__manual__">Enter a key manually</option>
          </select>
        </label>
        {manual && (
          <label className="span-2">
            Manual purpose key
            <input name="manualFeature" required pattern="[a-z][a-z0-9_.-]*" placeholder="billing.extract" />
          </label>
        )}
        {selectedPurpose && (
          <div className="span-2 route-purpose-context">
            <strong>{selectedPurpose.name}</strong>
            <span>{selectedPurpose.description || 'No description supplied by the application.'}</span>
            <div className="config-tags-group">
              {selectedPurpose.requiredCapabilities.map((capability) => <span className="config-tag" key={capability}>{capability.replace('_', ' ')}</span>)}
              {selectedPurpose.executionModes.map((mode) => <span className="config-tag" key={mode}>{mode}</span>)}
            </div>
          </div>
        )}
        <label className="span-2">
          Verified model
          <select name="modelId" required>
            <option value="">Select compatible model</option>
            {compatibleModels.map((model) => <option key={model.id} value={model.id}>{model.providerKey} / {model.modelKey}</option>)}
          </select>
          {selectedPurpose && compatibleModels.length === 0 && <small>No active model satisfies the required capabilities.</small>}
        </label>
        <label>Timeout (ms)<input name="timeout" type="number" min="1000" max="120000" defaultValue="45000" required /></label>
        <label>Max output tokens<input name="maxOutput" type="number" min="1" placeholder="Model default" /></label>
        <label className="span-2">Temperature<input name="temperature" type="number" min="0" max="2" step="0.1" defaultValue="0.2" required /></label>
        <button className="button button--primary span-2" disabled={busy || !selection || compatibleModels.length === 0}>
          {busy ? 'Saving...' : 'Save route'}
        </button>
      </form>
    </ModalShell>
  );
}

function ApplicationRegistry({
  applications,
  overview,
  onOpenApp,
  onNewApp,
  copiedId,
  onCopy,
  onToggleStatus,
  onDeleteApp,
}: {
  applications: Application[];
  overview: Overview;
  onOpenApp: (appId: string) => void;
  onNewApp: () => void;
  copiedId: string | null;
  onCopy: (text: string, id: string) => void;
  onToggleStatus: (app: Application) => void;
  onDeleteApp: (app: Application) => void;
}) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'suspended'>('all');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>(() => {
    try {
      return (localStorage.getItem('app_registry_view_mode') as 'list' | 'grid') || 'list';
    } catch {
      return 'list';
    }
  });

  const handleViewModeChange = (mode: 'list' | 'grid') => {
    setViewMode(mode);
    try {
      localStorage.setItem('app_registry_view_mode', mode);
    } catch {
      // ignore
    }
  };

  const totalKeys = applications.reduce((sum, app) => sum + (app.keyCount || 0), 0);
  const totalRoutes = applications.reduce((sum, app) => sum + (app.routeCount || 0), 0);
  const activeCount = applications.filter((app) => app.status === 'active').length;

  const filtered = applications.filter((app) => {
    const q = search.toLowerCase();
    const matchesSearch = !q || [app.name, app.slug, app.id].some((v) => v.toLowerCase().includes(q));
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="registry-container">
      <SectionTitle
        eyebrow="Application registry"
        title="Hard boundaries for every product."
        detail="Credentials, routes, and quotas are always attached to exactly one application. Select any boundary to open its dedicated configuration workspace."
        action={
          <button className="button button--primary" onClick={onNewApp}>
            <Plus size={17} /> New application
          </button>
        }
      />

      <section className="registry-kpi-bar">
        <article className="registry-kpi">
          <div className="registry-kpi-icon"><AppWindow /></div>
          <div className="registry-kpi-text">
            <span>Total applications</span>
            <strong>{applications.length}</strong>
          </div>
        </article>
        <article className="registry-kpi">
          <div className="registry-kpi-icon" style={{ color: '#326b58', background: '#e1efe8' }}><ShieldCheck /></div>
          <div className="registry-kpi-text">
            <span>Active boundaries</span>
            <strong>{activeCount}</strong>
          </div>
        </article>
        <article className="registry-kpi">
          <div className="registry-kpi-icon" style={{ color: '#8a6425', background: '#f5ead2' }}><KeyRound /></div>
          <div className="registry-kpi-text">
            <span>Scoped API keys</span>
            <strong>{totalKeys}</strong>
          </div>
        </article>
        <article className="registry-kpi">
          <div className="registry-kpi-icon" style={{ color: '#38677d', background: '#e1edf2' }}><RouteIcon /></div>
          <div className="registry-kpi-text">
            <span>Active routes</span>
            <strong>{totalRoutes}</strong>
          </div>
        </article>
      </section>

      <div className="registry-toolbar">
        <div className="registry-search">
          <Search size={16} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search applications by name, slug, or ID..."
            aria-label="Search applications"
          />
          {search && (
            <button className="icon-button" onClick={() => setSearch('')} aria-label="Clear search" style={{ width: 26, height: 26 }}>
              <X size={13} />
            </button>
          )}
        </div>
        <div className="registry-toolbar-controls">
          <div className="segmented">
            <button className={statusFilter === 'all' ? 'active' : ''} onClick={() => setStatusFilter('all')}>All ({applications.length})</button>
            <button className={statusFilter === 'active' ? 'active' : ''} onClick={() => setStatusFilter('active')}>Active ({activeCount})</button>
            <button className={statusFilter === 'suspended' ? 'active' : ''} onClick={() => setStatusFilter('suspended')}>Suspended ({applications.length - activeCount})</button>
          </div>
          <div className="segmented view-toggle">
            <button
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => handleViewModeChange('list')}
              title="List view (Space efficient)"
              aria-label="List view"
            >
              <LayoutList size={14} />
              <span>List</span>
            </button>
            <button
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => handleViewModeChange('grid')}
              title="Grid view"
              aria-label="Grid view"
            >
              <LayoutGrid size={14} />
              <span>Grid</span>
            </button>
          </div>
        </div>
      </div>

      {filtered.length ? (
        viewMode === 'list' ? (
          <div className="registry-list-panel">
            <div className="table-wrap">
              <table className="registry-table">
                <thead>
                  <tr>
                    <th>Application</th>
                    <th>Application ID</th>
                    <th>Status</th>
                    <th>Credentials</th>
                    <th>Routes</th>
                    <th>Economics</th>
                    <th>Registered</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((app) => {
                    const isCopied = copiedId === `reg-${app.id}`;
                    return (
                      <tr
                        key={app.id}
                        className="clickable-row registry-row"
                        onClick={() => onOpenApp(app.id)}
                        tabIndex={0}
                        role="button"
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpenApp(app.id); }}
                        title={`Open ${app.name} workspace`}
                      >
                        <td>
                          <div className="registry-row-app">
                            <span className="app-monogram-sm">{app.name.slice(0, 2).toUpperCase()}</span>
                            <div className="registry-row-identity">
                              <strong>{app.name}</strong>
                              <span className="registry-row-slug">{app.slug}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="registry-row-id-cell" onClick={(e) => e.stopPropagation()}>
                            <code title={app.id}>{app.id}</code>
                            <button
                              className={classNames('copy-btn copy-btn--sm', isCopied && 'copy-btn--active')}
                              onClick={() => onCopy(app.id, `reg-${app.id}`)}
                              title="Copy Application ID"
                              aria-label={`Copy ID for ${app.name}`}
                            >
                              {isCopied ? <Check size={11} /> : <Copy size={11} />}
                              <span>{isCopied ? 'Copied' : 'Copy'}</span>
                            </button>
                          </div>
                        </td>
                        <td>
                          <StatusPill status={app.status} />
                        </td>
                        <td>
                          <span className="registry-metric-chip" title={`${app.keyCount} scoped API keys`}>
                            <KeyRound size={13} />
                            <span>{app.keyCount} {app.keyCount === 1 ? 'key' : 'keys'}</span>
                          </span>
                        </td>
                        <td>
                          <span className="registry-metric-chip" title={`${app.routeCount} active model routes`}>
                            <RouteIcon size={13} />
                            <span>{app.routeCount} {app.routeCount === 1 ? 'route' : 'routes'}</span>
                          </span>
                        </td>
                        <td>
                          <div className="registry-row-pricing">
                            <span className="registry-margin-tag">{app.businessMarginFactor}x margin</span>
                            <small>{app.creditsPerUsd} cr / $</small>
                          </div>
                        </td>
                        <td>
                          <span className="registry-row-date" title={app.createdAt ? new Date(app.createdAt).toLocaleString() : ''}>
                            {dateTime(app.createdAt)}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                          <div className="registry-row-actions">
                            <button
                              className={classNames('icon-button app-card-action-btn', app.status === 'active' ? 'app-card-action-btn--warn' : 'app-card-action-btn--success')}
                              title={app.status === 'active' ? 'Suspend application' : 'Reactivate application'}
                              aria-label={app.status === 'active' ? `Suspend ${app.name}` : `Reactivate ${app.name}`}
                              onClick={() => onToggleStatus(app)}
                            >
                              {app.status === 'active' ? <PauseCircle size={14} /> : <PlayCircle size={14} />}
                            </button>
                            <button
                              className="icon-button app-card-action-btn app-card-action-btn--danger"
                              title="Delete application"
                              aria-label={`Delete ${app.name}`}
                              onClick={() => onDeleteApp(app)}
                            >
                              <Trash2 size={14} />
                            </button>
                            <button
                              className="registry-open-btn"
                              title={`Open ${app.name}`}
                              onClick={() => onOpenApp(app.id)}
                            >
                              <span>Open</span>
                              <ChevronRight size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="app-cards-grid">
            {filtered.map((app) => {
              const isCopied = copiedId === `reg-${app.id}`;
              return (
                <div
                  key={app.id}
                  className="app-card"
                  role="button"
                  tabIndex={0}
                  onClick={() => onOpenApp(app.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpenApp(app.id); }}
                >
                  <div className="app-card-header">
                    <div className="app-card-identity">
                      <div className="app-card-monogram">{app.name.slice(0, 2).toUpperCase()}</div>
                      <div className="app-card-name-slug">
                        <strong>{app.name}</strong>
                        <span className="app-card-slug">{app.slug}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <StatusPill status={app.status} />
                      <div className="app-card-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          className={classNames('icon-button app-card-action-btn', app.status === 'active' ? 'app-card-action-btn--warn' : 'app-card-action-btn--success')}
                          title={app.status === 'active' ? 'Suspend application' : 'Reactivate application'}
                          aria-label={app.status === 'active' ? `Suspend ${app.name}` : `Reactivate ${app.name}`}
                          onClick={() => onToggleStatus(app)}
                        >
                          {app.status === 'active' ? <PauseCircle size={14} /> : <PlayCircle size={14} />}
                        </button>
                        <button
                          className="icon-button app-card-action-btn app-card-action-btn--danger"
                          title="Delete application"
                          aria-label={`Delete ${app.name}`}
                          onClick={() => onDeleteApp(app)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="app-card-id-row" onClick={(e) => e.stopPropagation()}>
                    <code>{app.id}</code>
                    <button
                      className={classNames('copy-btn', isCopied && 'copy-btn--active')}
                      onClick={() => onCopy(app.id, `reg-${app.id}`)}
                      title="Copy Application ID"
                      aria-label="Copy Application ID"
                    >
                      {isCopied ? <Check size={11} /> : <Copy size={11} />}
                      <span>{isCopied ? 'Copied' : 'Copy'}</span>
                    </button>
                  </div>

                  <div className="app-card-metrics">
                    <div className="app-card-metric-item">
                      <KeyRound size={13} />
                      <span>Credentials</span>
                      <b>{app.keyCount}</b>
                    </div>
                    <div className="app-card-metric-item">
                      <RouteIcon size={13} />
                      <span>Model routes</span>
                      <b>{app.routeCount}</b>
                    </div>
                    <div className="app-card-metric-item">
                      <CircleDollarSign size={13} />
                      <span>Margin</span>
                      <b>{app.businessMarginFactor}x</b>
                    </div>
                    <div className="app-card-metric-item">
                      <Gauge size={13} />
                      <span>Credits / USD</span>
                      <b>{app.creditsPerUsd}</b>
                    </div>
                  </div>

                  <div className="app-card-footer">
                    <span>Registered {dateTime(app.createdAt)}</span>
                    <span className="app-card-action">
                      Open application <ChevronRight size={13} />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        <EmptyState
          icon={<AppWindow />}
          title={search || statusFilter !== 'all' ? 'No matching applications' : 'No applications registered'}
          detail={
            search || statusFilter !== 'all'
              ? 'Try adjusting your search query or status filter.'
              : 'Register your first product boundary to establish an isolated API scope.'
          }
        />
      )}
    </div>
  );
}

function ApiKeysPanel({
  keys,
  onIssueKey,
  onRevokeKey,
  onRollKey,
  onDeleteKey,
  copiedId,
  onCopy,
}: {
  keys: AppConfiguration['keys'];
  onIssueKey: () => void;
  onRevokeKey: (keyId: string) => void;
  onRollKey?: (keyId: string) => void;
  onDeleteKey?: (key: ApiKey) => void;
  copiedId: string | null;
  onCopy: (text: string, id: string) => void;
}) {
  const [revealedKeys, setRevealedKeys] = useState<Record<string, boolean>>({});

  const toggleReveal = (id: string) => {
    setRevealedKeys((curr) => ({ ...curr, [id]: !curr[id] }));
  };

  return (
    <article className="panel config-panel">
      <div className="panel-head">
        <div>
          <p className="eyebrow">Authentication</p>
          <h3>API keys</h3>
          <p className="panel-head-desc">
            Scoped credentials for this application's backend. Full keys are encrypted at rest with AES-GCM and can be copied or revealed anytime.
          </p>
        </div>
        <button className="button button--small button--primary" onClick={onIssueKey}>
          <Plus size={15} /> Issue key
        </button>
      </div>
      {keys.length ? (
        <div className="config-list">
          {keys.map((key) => {
            const isKeyCopied = copiedId === `key-${key.id}`;
            const isRevealed = Boolean(revealedKeys[key.id]);
            const hasFullKey = Boolean(key.apiKey);

            const handleCopyClick = () => {
              if (key.apiKey) {
                onCopy(key.apiKey, `key-${key.id}`);
                showToast({
                  type: 'success',
                  title: 'API Key Copied',
                  message: `Full API key for "${key.name}" copied to clipboard.`,
                });
              } else {
                onCopy(`aip_${key.prefix}`, `key-${key.id}`);
                showToast({
                  type: 'info',
                  title: 'Copied Prefix Only',
                  message: 'This key was created before full key encryption was enabled. Click the roll button to issue a full recoverable key.',
                });
              }
            };

            return (
              <div key={key.id} className="config-item">
                <div className="config-item-icon config-item-icon--key">
                  <KeyRound size={20} />
                </div>
                <div className="config-item-body">
                  <div className="config-item-title-row">
                    <span className="config-item-title">{key.name}</span>
                    <span
                      className="config-code-badge"
                      title={hasFullKey ? (isRevealed ? key.apiKey! : 'Click eye to reveal full key') : `Prefix: aip_${key.prefix}`}
                    >
                      {hasFullKey ? (isRevealed ? key.apiKey : `aip_${key.prefix}_...`) : `aip_${key.prefix}_...`}
                    </span>
                    {hasFullKey && (
                      <button
                        type="button"
                        className="copy-btn copy-btn--icon-only"
                        onClick={() => toggleReveal(key.id)}
                        title={isRevealed ? 'Hide full API key' : 'Reveal full API key'}
                        aria-label={isRevealed ? `Hide key for ${key.name}` : `Reveal key for ${key.name}`}
                      >
                        {isRevealed ? <EyeOff size={11} /> : <Eye size={11} />}
                      </button>
                    )}
                    <button
                      type="button"
                      className={classNames('copy-btn', isKeyCopied && 'copy-btn--active')}
                      onClick={handleCopyClick}
                      title={hasFullKey ? 'Copy full API key' : 'Copy prefix (legacy key)'}
                      aria-label={hasFullKey ? `Copy full API key for ${key.name}` : `Copy prefix for ${key.name}`}
                    >
                      {isKeyCopied ? <Check size={11} /> : <Copy size={11} />}
                      <span>{isKeyCopied ? 'Copied' : hasFullKey ? 'Copy' : 'Copy prefix'}</span>
                    </button>
                  </div>
                  <div className="config-item-meta">
                    <span className="config-meta-item">
                      <User size={12} className="meta-icon" />
                      <span>Created by <strong>{key.createdBy || 'Admin'}</strong></span>
                    </span>
                    <span className="config-meta-divider">•</span>
                    <span className="config-meta-item">
                      <CalendarClock size={12} className="meta-icon" />
                      <span>Created at <strong>{dateTime(key.createdAt)}</strong></span>
                    </span>
                    <span className="config-meta-divider">•</span>
                    <span className="config-meta-item">
                      <Clock3 size={12} className="meta-icon" />
                      <span>
                        Last used:{' '}
                        {key.lastUsedAt ? (
                          <strong style={{ color: 'var(--ink)' }}>{dateTime(key.lastUsedAt)}</strong>
                        ) : (
                          <strong style={{ color: '#9c4d3d' }}>Never</strong>
                        )}
                      </span>
                    </span>
                  </div>
                  {key.scopes.length > 0 && (
                    <div className="config-scopes-row">
                      <span className="config-scopes-label">Scopes</span>
                      <div className="config-tags-group">
                        {key.scopes.map((scope) => (
                          <span key={scope} className="config-tag">
                            {scope}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="config-item-controls">
                  <StatusPill status={key.status} />
                  {key.status === 'active' && onRollKey && (
                    <button
                      type="button"
                      className="icon-button icon-button--warn"
                      style={{ width: 32, height: 32 }}
                      title="Roll key (generates new secret, revokes old)"
                      aria-label={`Roll key ${key.name}`}
                      onClick={() => onRollKey(key.id)}
                    >
                      <RefreshCw size={14} />
                    </button>
                  )}
                  {key.status === 'active' ? (
                    <button
                      type="button"
                      className="icon-button icon-button--danger"
                      title="Revoke key"
                      aria-label={`Revoke ${key.name}`}
                      onClick={() => onRevokeKey(key.id)}
                    >
                      <X size={16} />
                    </button>
                  ) : (
                    onDeleteKey ? (
                      <button
                        type="button"
                        className="icon-button icon-button--danger"
                        style={{ width: 32, height: 32 }}
                        title="Delete revoked key"
                        aria-label={`Delete revoked key ${key.name}`}
                        onClick={() => onDeleteKey(key)}
                      >
                        <Trash2 size={14} />
                      </button>
                    ) : (
                      <span style={{ width: 34 }} />
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState
          icon={<KeyRound />}
          title="No credentials issued"
          detail="Issue a scoped API key for this application's trusted backend service."
        />
      )}
    </article>
  );
}

function ModelRoutesPanel({
  routes,
  onAddRoute,
  onToggleRoute,
  onDeleteRoute,
}: {
  routes: AppConfiguration['routes'];
  onAddRoute: (purposeKey?: string) => void;
  onToggleRoute: (routeId: string, enabled: boolean) => void;
  onDeleteRoute: (routeId: string, feature: string) => void;
}) {
  return (
    <article className="panel config-panel">
      <div className="panel-head">
        <div>
          <p className="eyebrow">Feature routing</p>
          <h3>Model routes</h3>
          <p className="panel-head-desc">
            Map abstract feature strings requested by your application to verified upstream AI models.
          </p>
        </div>
        <button className="button button--small button--primary" onClick={() => onAddRoute()}>
          <Plus size={15} /> Add route
        </button>
      </div>
      {routes.length ? (
        <div className="config-list">
          {routes.map((route) => (
            <div key={route.id} className="config-item">
              <div className="config-item-icon config-item-icon--route">
                <RouteIcon size={20} />
              </div>
              <div className="config-item-body">
                <div className="config-item-title-row">
                  <span className="config-code-badge" style={{ fontSize: '11.5px', fontWeight: 750, color: 'var(--ink)' }}>
                    {route.feature}
                  </span>
                  <span style={{ fontSize: '13px', color: 'var(--ink)', fontWeight: 750 }}>
                    {route.provider} / {route.model}
                  </span>
                </div>
                <div className="config-item-meta">
                  <span className="config-tag">{route.timeoutMs / 1000}s timeout</span>
                  {route.maxOutputTokens && (
                    <span className="config-tag">max {route.maxOutputTokens} tokens</span>
                  )}
                  {route.temperature !== undefined && (
                    <span className="config-tag">temp {route.temperature}</span>
                  )}
                </div>
              </div>
              <div className="config-item-controls">
                <StatusPill status={route.enabled ? 'active' : 'inactive'} />
                <div className="config-item-actions">
                  <button
                    className={classNames('icon-button', route.enabled ? 'icon-button--warn' : 'icon-button--success')}
                    title={route.enabled ? 'Disable route' : 'Enable route'}
                    aria-label={route.enabled ? `Disable ${route.feature}` : `Enable ${route.feature}`}
                    onClick={() => onToggleRoute(route.id, !route.enabled)}
                  >
                    <Power size={14} />
                  </button>
                  <button
                    className="icon-button icon-button--danger"
                    title="Delete route"
                    aria-label={`Delete ${route.feature}`}
                    onClick={() => onDeleteRoute(route.id, route.feature)}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={<RouteIcon />}
          title="No routes configured"
          detail="Map a generic feature string (e.g. chat.assistant) to a verified model."
        />
      )}
    </article>
  );
}

function PurposeRegistryPanel({
  purposes,
  manifest,
  onAssignModel,
}: {
  purposes: AppConfiguration['purposes'];
  manifest: AppConfiguration['purposeManifest'];
  onAssignModel: (purposeKey: string) => void;
}) {
  return (
    <article className="panel config-panel">
      <div className="panel-head">
        <div>
          <p className="eyebrow">Application contract</p>
          <h3>Purpose registry</h3>
          <p className="panel-head-desc">
            Purposes are declared by the application. Model choices remain controlled here.
          </p>
        </div>
        <div className="purpose-manifest-summary">
          {manifest ? (
            <>
              <strong>Revision {manifest.version}</strong>
              <span>Synced {dateTime(manifest.lastSyncedAt)}</span>
            </>
          ) : (
            <>
              <strong>No manifest synced</strong>
              <span>Manual routes remain supported</span>
            </>
          )}
        </div>
      </div>
      {purposes.length ? (
        <div className="config-list">
          {purposes.map((purpose) => (
            <div key={purpose.id} className="config-item purpose-item">
              <div className="config-item-icon config-item-icon--purpose">
                <RadioTower size={20} />
              </div>
              <div className="config-item-body">
                <div className="config-item-title-row">
                  <span className="config-item-title">{purpose.name}</span>
                  <span className="config-code-badge">{purpose.key}</span>
                  <StatusPill status={purpose.status} />
                </div>
                <p className="purpose-description">{purpose.description || 'No description supplied by the application.'}</p>
                <div className="config-scopes-row">
                  <span className="config-scopes-label">Needs</span>
                  <div className="config-tags-group">
                    {purpose.requiredCapabilities.map((capability) => <span className="config-tag" key={capability}>{capability.replace('_', ' ')}</span>)}
                    {purpose.executionModes.map((mode) => <span className="config-tag" key={mode}>{mode}</span>)}
                    <span className="config-tag">{purpose.source}</span>
                  </div>
                </div>
                <div className="purpose-route-state">
                  {purpose.routeId ? (
                    <span><strong>{purpose.provider} / {purpose.model}</strong>{purpose.routeEnabled ? 'Active route' : 'Route disabled'}</span>
                  ) : (
                    <span><strong>Unassigned</strong>No model route is configured.</span>
                  )}
                </div>
              </div>
              <div className="config-item-controls">
                {purpose.status === 'active' && (
                  <button className="button button--small button--subtle" onClick={() => onAssignModel(purpose.key)}>
                    {purpose.routeId ? 'Change model' : 'Assign model'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={<RadioTower />}
          title="No purposes registered"
          detail="Sync a manifest from the application backend, or create a manual model route."
        />
      )}
    </article>
  );
}

function QuotaPoliciesPanel({
  quotas,
  onSetQuota,
}: {
  quotas: AppConfiguration['quotas'];
  onSetQuota: () => void;
}) {
  return (
    <article className="panel config-panel">
      <div className="panel-head">
        <div>
          <p className="eyebrow">Budget guardrails</p>
          <h3>Quota policies</h3>
          <p className="panel-head-desc">
            Set cumulative monthly request and token limits across app, tenant, and end-user scopes.
          </p>
        </div>
        <button className="button button--small button--primary" onClick={onSetQuota}>
          <Plus size={15} /> Set quota
        </button>
      </div>
      {quotas.length ? (
        <div className="config-list">
          {quotas.map((quota) => (
            <div key={quota.id} className="config-item">
              <div className="config-item-icon config-item-icon--quota">
                <SlidersHorizontal size={20} />
              </div>
              <div className="config-item-body">
                <div className="config-item-title-row">
                  <span className="config-item-title">
                    {quota.scopeType === 'app' ? 'Entire application' : `${quota.scopeType}: ${quota.scopeId}`}
                  </span>
                  {quota.scopeId && <span className="config-code-badge">{quota.scopeId}</span>}
                </div>
                <div className="config-item-meta">
                  <span>
                    Requests: <strong>{quota.monthlyRequests ? `${compact(quota.monthlyRequests)}/mo` : 'Unlimited'}</strong>
                  </span>
                  <span>•</span>
                  <span>
                    Tokens: <strong>{quota.monthlyTokens ? `${compact(quota.monthlyTokens)}/mo` : 'Unlimited'}</strong>
                  </span>
                  {quota.monthlyProviderCostUsdMicro && (
                    <>
                      <span>•</span>
                      <span>
                        Cost cap: <strong>{money(quota.monthlyProviderCostUsdMicro)}</strong>
                      </span>
                    </>
                  )}
                  {quota.monthlyCredits && (
                    <>
                      <span>•</span>
                      <span>
                        Credits cap: <strong>{compact(quota.monthlyCredits)} cr</strong>
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="config-item-controls">
                <StatusPill status={quota.enabled ? 'active' : 'inactive'} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={<SlidersHorizontal />}
          title="No quota policies"
          detail="Set app, tenant, and end-user monthly limits independently to prevent runaways."
        />
      )}
    </article>
  );
}

function ApplicationDetail({
  application,
  configuration,
  overview,
  requests,
  onBack,
  onIssueKey,
  onAddRoute,
  onSetQuota,
  onRevokeKey,
  onRollKey,
  onDeleteKey,
  copiedId,
  onCopy,
  onToggleStatus,
  onDeleteApp,
  onToggleRoute,
  onDeleteRoute,
}: {
  application: Application;
  configuration: AppConfiguration;
  overview: Overview;
  requests: AiRequest[];
  onBack: () => void;
  onIssueKey: () => void;
  onAddRoute: (purposeKey?: string) => void;
  onSetQuota: () => void;
  onRevokeKey: (keyId: string) => void;
  onRollKey?: (keyId: string) => void;
  onDeleteKey?: (key: ApiKey) => void;
  copiedId: string | null;
  onCopy: (text: string, id: string) => void;
  onToggleStatus: (app: Application) => void;
  onDeleteApp: (app: Application) => void;
  onToggleRoute: (routeId: string, enabled: boolean) => void;
  onDeleteRoute: (routeId: string, feature: string) => void;
}) {
  const [activeTab, setActiveTab] = useState<'overview' | 'analytics' | 'keys' | 'purposes' | 'routes' | 'quotas' | 'requests'>('overview');
  const [requestSearch, setRequestSearch] = useState('');

  const appStats = overview.byApp.find((a) => a.appId === application.id);
  const appRequests = requests.filter((r) => r.appId === application.id);
  const visibleRequests = appRequests.filter((request) => {
    const s = requestSearch.toLowerCase();
    return !s || [request.id, request.tenantId, request.subjectId, request.feature, request.model].some((val) => val.toLowerCase().includes(s));
  });

  const activeKeysCount = configuration.keys.filter((k) => k.status === 'active').length;
  const activeRoutesCount = configuration.routes.filter((r) => r.enabled).length;
  const activeQuotasCount = configuration.quotas.filter((q) => q.enabled).length;
  const isCopied = copiedId === `detail-${application.id}`;
  const isSlugCopied = copiedId === `slug-${application.id}`;
  const isBoundaryIdCopied = copiedId === `boundary-${application.id}`;

  return (
    <div className="app-detail-container">
      <header className="panel app-detail-hero">
        <div className="app-detail-hero-left">
          <div className="app-detail-monogram-large">
            {application.name.slice(0, 2).toUpperCase()}
          </div>
          <div className="app-detail-hero-info">
            <div className="app-detail-title-line">
              <h2>{application.name}</h2>
              <span className="app-detail-slug-chip">{application.slug}</span>
              <StatusPill status={application.status} />
            </div>
            <div className="app-detail-meta-line">
              <span>Boundary ID:</span>
              <code>{application.id}</code>
              <button
                className={classNames('copy-btn', isCopied && 'copy-btn--active')}
                onClick={() => onCopy(application.id, `detail-${application.id}`)}
                title="Copy Application ID"
                aria-label="Copy Application ID"
              >
                {isCopied ? <Check size={11} /> : <Copy size={11} />}
                <span>{isCopied ? 'Copied' : 'Copy ID'}</span>
              </button>
              <span>•</span>
              <span>Registered {dateTime(application.createdAt)}</span>
            </div>
          </div>
        </div>

        <div className="app-detail-hero-actions">
          <div className="hero-action-group">
            <button className="button button--small button--primary" onClick={onIssueKey}>
              <Plus size={14} /> Issue key
            </button>
            <button className="button button--small button--subtle" onClick={() => onAddRoute()}>
              <Plus size={14} /> Add route
            </button>
            <button className="button button--small button--subtle" onClick={onSetQuota}>
              <Plus size={14} /> Set quota
            </button>
          </div>
          <span className="hero-actions-divider" />
          <div className="hero-action-group">
            <button
              className={classNames('button button--small', application.status === 'active' ? 'button--warning' : 'button--success')}
              onClick={() => onToggleStatus(application)}
              title={application.status === 'active' ? 'Suspend application' : 'Reactivate application'}
            >
              {application.status === 'active' ? <><PauseCircle size={14} /> Suspend</> : <><PlayCircle size={14} /> Reactivate</>}
            </button>
            <button
              className="button button--small button--danger-outline"
              onClick={() => onDeleteApp(application)}
              title="Delete application"
            >
              <Trash2 size={14} /> Delete
            </button>
          </div>
        </div>
      </header>

      {application.status === 'suspended' && (
        <div className="boundary-suspended-alert">
          <AlertTriangle size={20} />
          <div>
            <strong>Application boundary is suspended</strong>
            <p>All API keys under this application are currently blocked from making inference calls. Active routes and quotas remain preserved. Click "Reactivate" to restore traffic immediately.</p>
          </div>
          <button
            className="button button--small button--success"
            onClick={() => onToggleStatus(application)}
          >
            <PlayCircle size={14} /> Reactivate
          </button>
        </div>
      )}

      <section className="app-detail-kpis">
        <article className="detail-kpi-card">
          <div className="detail-kpi-icon" style={{ color: '#8a6425', background: '#f5ead2' }}>
            <KeyRound size={18} />
          </div>
          <div className="detail-kpi-body">
            <span>API Credentials</span>
            <strong>{activeKeysCount} active</strong>
            <small>{configuration.keys.length} total issued</small>
          </div>
        </article>

        <article className="detail-kpi-card">
          <div className="detail-kpi-icon" style={{ color: '#38677d', background: '#e1edf2' }}>
            <RouteIcon size={18} />
          </div>
          <div className="detail-kpi-body">
            <span>Model Routes</span>
            <strong>{activeRoutesCount} active</strong>
            <small>{configuration.routes.length} mapped endpoints</small>
          </div>
        </article>

        <article className="detail-kpi-card">
          <div className="detail-kpi-icon" style={{ color: '#326b58', background: '#e1efe8' }}>
            <SlidersHorizontal size={18} />
          </div>
          <div className="detail-kpi-body">
            <span>Quota Policies</span>
            <strong>{activeQuotasCount} active</strong>
            <small>{configuration.quotas.length} guardrails defined</small>
          </div>
        </article>

        <article className="detail-kpi-card">
          <div className="detail-kpi-icon" style={{ color: '#a84938', background: '#fae5df' }}>
            <CircleDollarSign size={18} />
          </div>
          <div className="detail-kpi-body">
            <span>Unit Economics</span>
            <strong>{application.businessMarginFactor}x margin</strong>
            <small>{application.creditsPerUsd} cr/$ · Min {application.minimumRequestCredits} cr</small>
          </div>
        </article>
      </section>

      <nav className="app-detail-tabs-bar" role="tablist">
        <button
          className={classNames('app-detail-tab', activeTab === 'overview' && 'active')}
          onClick={() => setActiveTab('overview')}
          role="tab"
          aria-selected={activeTab === 'overview'}
        >
          <LayoutDashboard size={15} />
          <span>Overview</span>
        </button>
        <button
          className={classNames('app-detail-tab', activeTab === 'analytics' && 'active')}
          onClick={() => setActiveTab('analytics')}
          role="tab"
          aria-selected={activeTab === 'analytics'}
        >
          <TrendingUp size={15} />
          <span>Analytics</span>
        </button>
        <button
          className={classNames('app-detail-tab', activeTab === 'keys' && 'active')}
          onClick={() => setActiveTab('keys')}
          role="tab"
          aria-selected={activeTab === 'keys'}
        >
          <KeyRound size={15} />
          <span>API keys</span>
          <span className="tab-badge">{configuration.keys.length}</span>
        </button>
        <button
          className={classNames('app-detail-tab', activeTab === 'purposes' && 'active')}
          onClick={() => setActiveTab('purposes')}
          role="tab"
          aria-selected={activeTab === 'purposes'}
        >
          <RadioTower size={15} />
          <span>Purposes</span>
          <span className="tab-badge">{configuration.purposes.length}</span>
        </button>
        <button
          className={classNames('app-detail-tab', activeTab === 'routes' && 'active')}
          onClick={() => setActiveTab('routes')}
          role="tab"
          aria-selected={activeTab === 'routes'}
        >
          <RouteIcon size={15} />
          <span>Model routes</span>
          <span className="tab-badge">{configuration.routes.length}</span>
        </button>
        <button
          className={classNames('app-detail-tab', activeTab === 'quotas' && 'active')}
          onClick={() => setActiveTab('quotas')}
          role="tab"
          aria-selected={activeTab === 'quotas'}
        >
          <SlidersHorizontal size={15} />
          <span>Quota policies</span>
          <span className="tab-badge">{configuration.quotas.length}</span>
        </button>
        <button
          className={classNames('app-detail-tab', activeTab === 'requests' && 'active')}
          onClick={() => setActiveTab('requests')}
          role="tab"
          aria-selected={activeTab === 'requests'}
        >
          <Activity size={15} />
          <span>Request ledger</span>
          <span className="tab-badge">{appRequests.length}</span>
        </button>
      </nav>

      {activeTab === 'overview' && (
        <div className="app-tab-panel">
          <div className="configuration-stack">
            <ApiKeysPanel
              keys={configuration.keys}
              onIssueKey={onIssueKey}
              onRevokeKey={onRevokeKey}
              onRollKey={onRollKey}
              onDeleteKey={onDeleteKey}
              copiedId={copiedId}
              onCopy={onCopy}
            />
            <PurposeRegistryPanel
              purposes={configuration.purposes}
              manifest={configuration.purposeManifest}
              onAssignModel={onAddRoute}
            />
            <ModelRoutesPanel
              routes={configuration.routes}
              onAddRoute={onAddRoute}
              onToggleRoute={onToggleRoute}
              onDeleteRoute={onDeleteRoute}
            />
            <QuotaPoliciesPanel
              quotas={configuration.quotas}
              onSetQuota={onSetQuota}
            />
          </div>

          <div className="overview-two-col">
            <article className="panel boundary-panel">
              <div className="panel-head">
                <div>
                  <p className="eyebrow">Boundary partition</p>
                  <h3>Isolation & Economics</h3>
                </div>
                <div className="panel-head-actions">
                  <button
                    type="button"
                    className={classNames('button button--small', application.status === 'active' ? 'button--warning' : 'button--success')}
                    onClick={() => onToggleStatus(application)}
                    title={application.status === 'active' ? 'Suspend application boundary' : 'Reactivate application boundary'}
                  >
                    {application.status === 'active' ? <><PauseCircle size={13} /> Suspend</> : <><PlayCircle size={13} /> Reactivate</>}
                  </button>
                  <button
                    type="button"
                    className="button button--small button--danger-outline"
                    onClick={() => onDeleteApp(application)}
                    title="Delete application boundary"
                  >
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </div>

              <div className="boundary-card-body">
                <div className="boundary-specs">
                  <div className="spec-row">
                    <span className="spec-label">Display name</span>
                    <div className="spec-value-group">
                      <strong className="spec-value-name">{application.name}</strong>
                      <StatusPill status={application.status} />
                    </div>
                  </div>

                  <div className="spec-row">
                    <span className="spec-label">Routing slug</span>
                    <div className="spec-value-group">
                      <code className="spec-code">{application.slug}</code>
                      <button
                        type="button"
                        className={classNames('copy-btn', isSlugCopied && 'copy-btn--active')}
                        onClick={() => onCopy(application.slug, `slug-${application.id}`)}
                        title="Copy slug to clipboard"
                        aria-label="Copy slug to clipboard"
                      >
                        {isSlugCopied ? <Check size={11} /> : <Copy size={11} />}
                        <span>{isSlugCopied ? 'Copied' : 'Copy'}</span>
                      </button>
                    </div>
                  </div>

                  <div className="spec-row">
                    <span className="spec-label">Boundary ID</span>
                    <div className="spec-value-group">
                      <code className="spec-code spec-code--id" title={application.id}>{application.id}</code>
                      <button
                        type="button"
                        className={classNames('copy-btn', isBoundaryIdCopied && 'copy-btn--active')}
                        onClick={() => onCopy(application.id, `boundary-${application.id}`)}
                        title="Copy Boundary ID to clipboard"
                        aria-label="Copy Boundary ID to clipboard"
                      >
                        {isBoundaryIdCopied ? <Check size={11} /> : <Copy size={11} />}
                        <span>{isBoundaryIdCopied ? 'Copied' : 'Copy'}</span>
                      </button>
                    </div>
                  </div>

                  <div className="spec-row">
                    <span className="spec-label">Provisioned</span>
                    <span className="spec-value-group spec-value--muted">
                      <Clock3 size={12} className="spec-icon-inline" /> {dateTime(application.createdAt)}
                    </span>
                  </div>
                </div>

                <div className="bottom-metric-section">
                  <div className="bottom-section-header">
                    <CircleDollarSign size={13} className="bottom-header-icon" />
                    <span>Credit & Monetization Model</span>
                  </div>
                  <div className="metric-tiles-grid">
                    <div className="metric-tile">
                      <span className="metric-tile-label">Exchange rate</span>
                      <strong className="metric-tile-val">{application.creditsPerUsd} <small>credits</small></strong>
                      <span className="metric-tile-sub">per $1.00 USD</span>
                    </div>
                    <div className="metric-tile">
                      <span className="metric-tile-label">Margin factor</span>
                      <strong className="metric-tile-val">{application.businessMarginFactor}x</strong>
                      <span className="metric-tile-sub">raw cost multiplier</span>
                    </div>
                    <div className="metric-tile">
                      <span className="metric-tile-label">Minimum floor</span>
                      <strong className="metric-tile-val">{application.minimumRequestCredits} <small>cr</small></strong>
                      <span className="metric-tile-sub">floor debit per call</span>
                    </div>
                  </div>
                </div>
              </div>
            </article>

            <article className="panel security-panel">
              <div className="panel-head">
                <div>
                  <p className="eyebrow">Security posture</p>
                  <h3>Boundary Enforcement</h3>
                </div>
                <span className="live live--boundary"><i />Isolated</span>
              </div>

              <div className="security-card-body">
                <div className="security-safeguards">
                  <div className="safeguard-card">
                    <div className="safeguard-icon-box safeguard-icon-box--mint">
                      <ShieldCheck size={17} />
                    </div>
                    <div className="safeguard-info">
                      <div className="safeguard-title-row">
                        <strong>Credential boundary isolation</strong>
                        <span className="safeguard-tag safeguard-tag--active">
                          <Check size={10} /> Enforced
                        </span>
                      </div>
                      <p>Only API keys minted specifically for this boundary partition can resolve configured routes.</p>
                    </div>
                  </div>

                  <div className="safeguard-card">
                    <div className="safeguard-icon-box safeguard-icon-box--gold">
                      <LockKeyhole size={17} />
                    </div>
                    <div className="safeguard-info">
                      <div className="safeguard-title-row">
                        <strong>One-way secret storage</strong>
                        <span className="safeguard-tag safeguard-tag--active">
                          <Check size={10} /> Active
                        </span>
                      </div>
                      <p>API secrets are never stored in plaintext. Only cryptographic SHA-256 hashes and key prefixes are retained.</p>
                    </div>
                  </div>
                </div>

                <div className="bottom-metric-section">
                  <div className="bottom-section-header">
                    <Activity size={13} className="bottom-header-icon" />
                    <span>Trailing 30-Day Activity Window</span>
                  </div>
                  {appStats ? (
                    <div className="metric-tiles-grid">
                      <div className="metric-tile">
                        <span className="metric-tile-label">Inference calls</span>
                        <strong className="metric-tile-val">{compact(appStats.requests)}</strong>
                        <span className="metric-tile-sub">Total volume</span>
                      </div>
                      <div className="metric-tile">
                        <span className="metric-tile-label">Provider spend</span>
                        <strong className="metric-tile-val">{money(appStats.providerCostUsdMicro)}</strong>
                        <span className="metric-tile-sub">Raw cost</span>
                      </div>
                      <div className="metric-tile">
                        <span className="metric-tile-label">Error rate</span>
                        <strong className={classNames('metric-tile-val', appStats.errorRate > 0 ? 'metric-tile-val--warn' : 'metric-tile-val--good')}>
                          {appStats.errorRate.toFixed(1)}%
                        </strong>
                        <span className="metric-tile-sub">{appStats.errorRate === 0 ? 'Optimal' : 'Elevated'}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="metric-empty-notice">
                      <span>No inference calls logged for this boundary in the trailing 30 days.</span>
                    </div>
                  )}
                </div>
              </div>
            </article>
          </div>

          <article className="panel">
            <div className="panel-head">
              <div>
                <p className="eyebrow">Attributable activity</p>
                <h3>Recent inference calls</h3>
              </div>
              {appRequests.length > 0 && (
                <button className="text-button" onClick={() => setActiveTab('requests')}>
                  View full ledger ({appRequests.length}) <ArrowUpRight size={15} />
                </button>
              )}
            </div>
            {appRequests.length ? (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Request</th>
                      <th>Feature</th>
                      <th>Model</th>
                      <th>Input</th>
                      <th>Latency</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appRequests.slice(0, 5).map((req) => (
                      <tr key={req.id}>
                        <td><strong className="request-id">{req.id.slice(0, 8)}</strong><small>{dateTime(req.createdAt)}</small></td>
                        <td><code>{req.feature}</code></td>
                        <td>{req.provider}<small>{req.model}</small></td>
                        <td><span className="input-flags">{req.hasFiles ? <FileImage size={16} /> : 'Text'}{req.hasResponseSchema ? <span>JSON</span> : null}</span></td>
                        <td>{req.latencyMs === null ? '—' : `${compact(req.latencyMs)} ms`}</td>
                        <td><StatusPill status={req.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState icon={<Activity />} title="No requests recorded yet" detail="Attributable API calls will appear here once backend integration begins." />
            )}
          </article>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="app-tab-panel">
          <AnalyticsTab
            application={application}
            configuration={configuration}
          />
        </div>
      )}

      {activeTab === 'keys' && (
        <ApiKeysPanel
          keys={configuration.keys}
          onIssueKey={onIssueKey}
          onRevokeKey={onRevokeKey}
          onRollKey={onRollKey}
          onDeleteKey={onDeleteKey}
          copiedId={copiedId}
          onCopy={onCopy}
        />
      )}

      {activeTab === 'routes' && (
        <ModelRoutesPanel
          routes={configuration.routes}
          onAddRoute={onAddRoute}
          onToggleRoute={onToggleRoute}
          onDeleteRoute={onDeleteRoute}
        />
      )}

      {activeTab === 'purposes' && (
        <PurposeRegistryPanel
          purposes={configuration.purposes}
          manifest={configuration.purposeManifest}
          onAssignModel={onAddRoute}
        />
      )}

      {activeTab === 'quotas' && (
        <QuotaPoliciesPanel
          quotas={configuration.quotas}
          onSetQuota={onSetQuota}
        />
      )}

      {activeTab === 'requests' && (
        <article className="panel request-ledger">
          <div className="ledger-tools">
            <label>
              <Search size={17} />
              <input
                value={requestSearch}
                onChange={(e) => setRequestSearch(e.target.value)}
                placeholder="Search by ID, tenant, subject, feature, or model..."
              />
            </label>
            <span>{visibleRequests.length} records</span>
          </div>
          {visibleRequests.length ? (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Request ID</th>
                    <th>Tenant / Subject</th>
                    <th>Feature</th>
                    <th>Model</th>
                    <th>Input</th>
                    <th>Latency</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRequests.map((req) => (
                    <tr key={req.id}>
                      <td><strong className="request-id">{req.id.slice(0, 8)}</strong><small>{dateTime(req.createdAt)}</small></td>
                      <td><strong>{req.tenantId}</strong><small>{req.subjectId}</small></td>
                      <td><code>{req.feature}</code></td>
                      <td>{req.provider}<small>{req.model}</small></td>
                      <td><span className="input-flags">{req.hasFiles ? <FileImage size={16} /> : 'Text'}{req.hasResponseSchema ? <span>JSON</span> : null}</span></td>
                      <td>{req.latencyMs === null ? '—' : `${compact(req.latencyMs)} ms`}</td>
                      <td><StatusPill status={req.status} />{req.errorCode && <small className="error-code">{req.errorCode}</small>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState icon={<Activity />} title="No matching requests" detail="Requests executed under this application boundary will be recorded here." />
          )}
        </article>
      )}
    </div>
  );
}

export default function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [section, setSection] = useState<Section>('overview');
  const [mobileNav, setMobileNav] = useState(false);
  const [overview, setOverview] = useState(EMPTY_OVERVIEW);
  const [applications, setApplications] = useState<Application[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [requests, setRequests] = useState<AiRequest[]>([]);
  const [configuration, setConfiguration] = useState<AppConfiguration>({ keys: [], purposes: [], purposeManifest: null, routes: [], quotas: [] });
  const [filterApp, setFilterApp] = useState('');
  const [selectedApp, setSelectedApp] = useState('');
  const [openedAppId, setOpenedAppId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [requestSearch, setRequestSearch] = useState('');
  const [modal, setModal] = useState<Modal>(null);
  const [deleteAppTarget, setDeleteAppTarget] = useState<Application | null>(null);
  const [deleteAppConfirmSlug, setDeleteAppConfirmSlug] = useState('');
  const [deleteRouteTarget, setDeleteRouteTarget] = useState<{ routeId: string; feature: string } | null>(null);
  const [deleteKeyTarget, setDeleteKeyTarget] = useState<ApiKey | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState('');
  const [noticeError, setNoticeError] = useState(false);
  const [issuedKey, setIssuedKey] = useState('');
  const [routePurposeKey, setRoutePurposeKey] = useState('');
  const [applicationError, setApplicationError] = useState('');

  function handleError(error: unknown) {
    if (error instanceof AdminApiError && error.status === 401) {
      setAuthenticated(false); return;
    }
    const message = error instanceof Error ? error.message : 'The operation failed.';
    setNoticeError(true);
    setNotice(message);
    showToast({ type: 'error', title: 'Action Failed', message });
  }

  useEffect(() => {
    if (notice) {
      const timer = setTimeout(() => setNotice(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [notice]);

  async function loadCore() {
    const [nextApps, nextModels] = await Promise.all([
      adminRequest<Application[]>('/applications'),
      adminRequest<Model[]>('/models'),
    ]);
    setApplications(nextApps); setModels(nextModels);
    setSelectedApp((current) => current || nextApps[0]?.id || '');
  }

  async function loadActivity() {
    const query = filterApp ? `&appId=${encodeURIComponent(filterApp)}` : '';
    const [nextOverview, nextRequests] = await Promise.all([
      adminRequest<Overview>(`/overview?days=${days}${query}`),
      adminRequest<AiRequest[]>(`/requests?limit=100${query}`),
    ]);
    setOverview(nextOverview); setRequests(nextRequests);
  }

  async function loadConfiguration(appId: string) {
    if (!appId) { setConfiguration({ keys: [], purposes: [], purposeManifest: null, routes: [], quotas: [] }); return; }
    const [keys, purposeRegistry, routes, quotas] = await Promise.all([
      adminRequest<ApiKey[]>(`/applications/${appId}/keys`),
      adminRequest<PurposeRegistry>(`/applications/${appId}/purposes`),
      adminRequest<Route[]>(`/applications/${appId}/routes`),
      adminRequest<Quota[]>(`/applications/${appId}/quotas`),
    ]);
    setConfiguration({ keys, purposes: purposeRegistry.purposes, purposeManifest: purposeRegistry.manifest, routes, quotas });
  }

  useEffect(() => {
    if (!authenticated) return;
    setLoading(true);
    Promise.all([loadCore(), loadActivity()]).catch(handleError).finally(() => setLoading(false));
  }, [authenticated]);

  useEffect(() => {
    if (!authenticated) return;
    setLoading(true); loadActivity().catch(handleError).finally(() => setLoading(false));
  }, [filterApp, days]);

  useEffect(() => {
    if (!authenticated || !selectedApp) return;
    loadConfiguration(selectedApp).catch(handleError);
  }, [authenticated, selectedApp]);

  useEffect(() => {
    function syncFromHash() {
      const hash = window.location.hash;
      if (hash.startsWith('#/applications/')) {
        const appId = decodeURIComponent(hash.replace('#/applications/', '').trim());
        if (appId) {
          setSection('applications');
          setOpenedAppId(appId);
          setSelectedApp(appId);
          return;
        }
      }
      if (hash === '#/applications') {
        setSection('applications');
        setOpenedAppId(null);
        return;
      }
      if (hash === '#/overview' || hash === '#/models' || hash === '#/operations' || hash === '#/requests') {
        setSection(hash.replace('#/', '') as Section);
        setOpenedAppId(null);
      }
    }

    syncFromHash();
    window.addEventListener('hashchange', syncFromHash);
    return () => window.removeEventListener('hashchange', syncFromHash);
  }, []);

  function copyToClipboard(text: string, id: string) {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {
        fallbackCopy(text);
      });
    } else {
      fallbackCopy(text);
    }
    setCopiedId(id);
    setTimeout(() => {
      setCopiedId((current) => (current === id ? null : current));
    }, 2200);
  }

  function fallbackCopy(text: string) {
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.style.position = 'fixed';
      el.style.left = '-9999px';
      el.style.top = '0';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.focus();
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    } catch {
      // Ignore clipboard write failures
    }
  }

  function openApplication(appId: string) {
    setOpenedAppId(appId);
    setSelectedApp(appId);
    setSection('applications');
    window.location.hash = `#/applications/${encodeURIComponent(appId)}`;
  }

  function closeApplication() {
    setOpenedAppId(null);
    window.location.hash = '#/applications';
  }

  async function refresh() {
    setLoading(true);
    try { await Promise.all([loadCore(), loadActivity(), loadConfiguration(selectedApp)]); }
    catch (error) { handleError(error); }
    finally { setLoading(false); }
  }

  async function execute(operation: () => Promise<unknown>, message: string, close = true) {
    setBusy(true); setNotice(''); setNoticeError(false);
    try {
      await operation();
      if (close) setModal(null);
      setNotice(message);
      showToast({ type: 'success', title: 'Success', message });
      await refresh();
    }
    catch (error) { handleError(error); }
    finally { setBusy(false); }
  }

  async function handleRollKey(appId: string, keyId: string) {
    await execute(
      async () => {
        const result = await adminRequest<{ id: string; apiKey: string; name: string }>(
          `/applications/${appId}/keys/${keyId}/roll`,
          { method: 'POST' }
        );
        setIssuedKey(result.apiKey);
        setModal('key');
        copyToClipboard(result.apiKey, 'modal-credential');
        showToast({
          type: 'success',
          title: 'Key Rolled',
          message: `API key for "${result.name}" rotated. New key copied to clipboard.`,
        });
      },
      'API key rolled successfully.',
      false
    );
  }

  async function handleToggleAppStatus(app: Application) {
    const nextStatus = app.status === 'active' ? 'suspended' : 'active';
    await execute(
      async () => {
        await adminRequest(`/applications/${app.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ status: nextStatus }),
        });
      },
      `Application ${app.name} ${nextStatus === 'suspended' ? 'suspended' : 'reactivated'}.`,
      false
    );
  }

  function handleDeleteApp(app: Application) {
    setDeleteAppConfirmSlug('');
    setDeleteAppTarget(app);
  }

  async function confirmDeleteApplication() {
    if (!deleteAppTarget || deleteAppConfirmSlug !== deleteAppTarget.slug) return;
    const targetId = deleteAppTarget.id;
    const targetName = deleteAppTarget.name;
    await execute(
      async () => {
        await adminRequest(`/applications/${targetId}`, {
          method: 'DELETE',
        });
        if (openedAppId === targetId) {
          setOpenedAppId(null);
          window.location.hash = '#/applications';
        }
      },
      `Application ${targetName} permanently deleted.`
    );
    setDeleteAppTarget(null);
    setDeleteAppConfirmSlug('');
    setCopiedId((curr) => (curr === 'delete-app-slug' ? null : curr));
  }

  async function handleToggleRoute(routeId: string, enabled: boolean) {
    const appId = openedAppId || selectedApp;
    if (!appId) return;
    await execute(
      async () => {
        await adminRequest(`/applications/${appId}/routes/${routeId}`, {
          method: 'PATCH',
          body: JSON.stringify({ enabled }),
        });
      },
      `Model route ${enabled ? 'enabled' : 'disabled'}.`,
      false
    );
  }

  function handleDeleteRoute(routeId: string, feature: string) {
    setDeleteRouteTarget({ routeId, feature });
  }

  async function confirmDeleteRoute() {
    const appId = openedAppId || selectedApp;
    if (!appId || !deleteRouteTarget) return;
    const { routeId, feature } = deleteRouteTarget;
    await execute(
      async () => {
        await adminRequest(`/applications/${appId}/routes/${routeId}`, {
          method: 'DELETE',
        });
      },
      `Model route for ${feature} deleted.`,
      true
    );
    setDeleteRouteTarget(null);
  }

  function handleDeleteKey(key: ApiKey) {
    setDeleteKeyTarget(key);
  }

  async function confirmDeleteKey() {
    const appId = openedAppId || selectedApp;
    if (!appId || !deleteKeyTarget) return;
    const targetKey = deleteKeyTarget;
    await execute(
      async () => {
        await adminRequest(`/applications/${appId}/keys/${targetKey.id}?permanent=true`, {
          method: 'DELETE',
        });
      },
      `Revoked API key "${targetKey.name}" permanently deleted.`
    );
    setDeleteKeyTarget(null);
  }

  function logout() { window.location.assign('/cdn-cgi/access/logout'); }
  if (!authenticated) return <Login onAuthenticated={() => setAuthenticated(true)} />;

  const activeApp = openedAppId ? applications.find((application) => application.id === openedAppId) || null : null;
  const selected = applications.find((application) => application.id === selectedApp);
  const visibleRequests = requests.filter((request) => {
    const search = requestSearch.toLowerCase();
    return !search || [request.id, request.tenantId, request.subjectId, request.feature, request.model].some((value) => value.toLowerCase().includes(search));
  });
  const successRate = overview.totals.requests ? (overview.totals.completed / overview.totals.requests) * 100 : 100;
  const posture = successRate >= 99 ? 'Operational' : successRate >= 90 ? 'Watch' : 'Degraded';
  const navigation: Array<{ id: Section; label: string; icon: ReactNode }> = [
    { id: 'overview', label: 'Overview', icon: <LayoutDashboard size={19} /> },
    { id: 'applications', label: 'Applications', icon: <AppWindow size={19} /> },
    { id: 'models', label: 'Models & pricing', icon: <Bot size={19} /> },
    { id: 'operations', label: 'Push-to-Sync', icon: <RadioTower size={19} /> },
    { id: 'requests', label: 'Request ledger', icon: <Activity size={19} /> },
  ];

  return (
    <div className="app-shell">
      <aside className={classNames('sidebar', mobileNav && 'sidebar--open')}>
        <div className="sidebar-brand"><div className="brand-mark"><Boxes size={22} /><span>relay</span></div><button className="icon-button mobile-only" onClick={() => setMobileNav(false)} aria-label="Close navigation"><X size={20} /></button></div>
        <div className="environment"><span />Development edge</div>
        <nav>{navigation.map((item) => <button key={item.id} className={classNames(section === item.id && 'active')} onClick={() => { setSection(item.id); setMobileNav(false); if (item.id === 'applications') { setOpenedAppId(null); window.location.hash = '#/applications'; } else { window.location.hash = `#/${item.id}`; } }}>{item.icon}<span>{item.label}</span><ChevronRight size={15} /></button>)}</nav>
        <div className="sidebar-foot">
          <div className="security-note"><ShieldCheck size={18} /><div><strong>Security posture</strong><span>App scopes enforced</span></div></div>
          <button className="logout" onClick={logout}><LogOut size={18} />End session</button>
        </div>
      </aside>

      <main className="workspace">
        <header className="topbar">
          <button className="icon-button mobile-only" onClick={() => setMobileNav(true)} aria-label="Open navigation"><Menu size={21} /></button>
          <div className="topbar-title">
            <span>AI Platform</span>
            <ChevronRight size={14} />
            {section === 'applications' && activeApp ? (
              <>
                <button className="breadcrumb-link" onClick={closeApplication}>Applications</button>
                <ChevronRight size={14} />
                <strong>{activeApp.name}</strong>
              </>
            ) : (
              <strong>{navigation.find((item) => item.id === section)?.label}</strong>
            )}
          </div>
          <div className="topbar-actions">
            <select
              value={section === 'applications' && activeApp ? activeApp.id : filterApp}
              onChange={(event) => {
                const val = event.target.value;
                setFilterApp(val);
                if (section === 'applications') {
                  if (val) openApplication(val);
                  else closeApplication();
                }
              }}
              aria-label="Filter by application"
            >
              <option value="">All applications</option>
              {applications.map((application) => (
                <option key={application.id} value={application.id}>{application.name}</option>
              ))}
            </select>
            <button className="icon-button" onClick={refresh} aria-label="Refresh" disabled={loading}><RefreshCw size={18} className={loading ? 'spin' : ''} /></button>
            <div className="avatar">OP</div>
          </div>
        </header>

        <ToastContainer />

        {notice && <div className={classNames('notice', noticeError && 'notice--error')}>{noticeError ? <TriangleAlert size={16} /> : <Check size={16} />}<span>{notice}</span><button onClick={() => setNotice('')} aria-label="Dismiss notification"><X size={15} /></button></div>}

        <div className={classNames('content', loading && 'content--loading')}>
          {section === 'overview' && <>
            <SectionTitle eyebrow="Control plane" title="Operational clarity, without the noise." detail="Monitor usage, reliability, and model economics across every connected application." action={<div className="segmented">{[7, 30, 90].map((value) => <button key={value} className={days === value ? 'active' : ''} onClick={() => setDays(value)}>{value}d</button>)}</div>} />
            <section className="metrics">
              <article><div className="metric-icon metric-icon--green"><Activity /></div><span>Total requests</span><strong>{compact(overview.totals.requests)}</strong><small>{compact(overview.totals.inputTokens + overview.totals.outputTokens)} tokens processed</small></article>
              <article><div className="metric-icon metric-icon--coral"><CircleDollarSign /></div><span>Provider spend</span><strong>{money(overview.totals.providerCostUsdMicro)}</strong><small>{compact(overview.totals.billedCredits)} customer credits</small></article>
              <article><div className="metric-icon metric-icon--blue"><Gauge /></div><span>Success rate</span><strong>{successRate.toFixed(1)}%</strong><small>{overview.totals.failed} failed or blocked</small></article>
              <article><div className="metric-icon metric-icon--gold"><Clock3 /></div><span>Average latency</span><strong>{compact(overview.totals.averageLatencyMs)}<i>ms</i></strong><small>Provider round trip</small></article>
            </section>
            <section className="dashboard-grid">
              <article className="panel panel--chart"><div className="panel-head"><div><p className="eyebrow">Traffic pulse</p><h3>Request volume</h3></div><span className="legend"><i />Requests</span></div><UsageChart overview={overview} /></article>
              <article className="panel health-panel"><div className="panel-head"><div><p className="eyebrow">Platform health</p><h3>Live posture</h3></div><span className={classNames('live', posture !== 'Operational' && 'live--warn')}><i />{posture}</span></div>
                <div className="health-score"><div style={{ '--score': `${successRate * 3.6}deg` } as React.CSSProperties}><span>{successRate.toFixed(0)}<small>%</small></span></div><p>Successful completion rate</p></div>
                <dl><div><dt>Active applications</dt><dd>{overview.totals.applications}</dd></div><div><dt>Completed requests</dt><dd>{compact(overview.totals.completed)}</dd></div><div><dt>Median target</dt><dd>&lt; 4.0s</dd></div></dl>
              </article>
            </section>
            <section className="dashboard-grid dashboard-grid--tables">
              <article className="panel"><div className="panel-head"><div><p className="eyebrow">Isolation view</p><h3>Applications</h3></div><button className="text-button" onClick={() => { setSection('applications'); setOpenedAppId(null); window.location.hash = '#/applications'; }}>Registry <ArrowUpRight size={15} /></button></div>
                {overview.byApp.length ? <div className="table-wrap"><table><thead><tr><th>Application</th><th>Requests</th><th>Spend</th><th>Error rate</th><th></th></tr></thead><tbody>{overview.byApp.map((row) => <tr key={row.appId} className="clickable-row" onClick={() => openApplication(row.appId)} title={`Open ${row.appName}`}><td><span className="app-ident"><i>{row.appName.slice(0, 2).toUpperCase()}</i><span><strong>{row.appName}</strong><small>{row.appSlug}</small></span></span></td><td>{compact(row.requests)}</td><td>{money(row.providerCostUsdMicro)}</td><td><span className={classNames('rate', row.errorRate > 5 && 'rate--warn')}>{row.errorRate.toFixed(1)}%</span></td><td style={{ textAlign: 'right' }}><span className="row-action-link">Open <ChevronRight size={14} /></span></td></tr>)}</tbody></table></div> : <EmptyState icon={<AppWindow />} title="No applications yet" detail="Register the first application to establish an isolated API scope." />}
              </article>
              <article className="panel"><div className="panel-head"><div><p className="eyebrow">Economics</p><h3>Model mix</h3></div><button className="text-button" onClick={() => setSection('models')}>Pricing <ArrowUpRight size={15} /></button></div>
                {overview.byModel.length ? <div className="model-mix">{overview.byModel.slice(0, 5).map((row, index) => <div key={`${row.provider}:${row.model}`}><span className="model-rank">0{index + 1}</span><span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><ProviderBadge provider={row.provider} modelKey={row.model} size={14} badgeSize={24} /><span><strong>{row.model}</strong><small>{row.provider} · {compact(row.requests)} calls</small></span></span><b>{money(row.providerCostUsdMicro)}</b></div>)}</div> : <EmptyState icon={<Bot />} title="No model usage yet" detail="Verified provider costs will be reconciled here after the first request." />}
                {overview.byFeature.length > 0 && <div className="feature-strip"><span>Top features</span>{overview.byFeature.slice(0, 3).map((row) => <code key={row.feature}>{row.feature} <b>{row.requests}</b></code>)}</div>}
              </article>
            </section>
          </>}

          {section === 'applications' && (
            activeApp ? (
              <ApplicationDetail
                application={activeApp}
                configuration={configuration}
                overview={overview}
                requests={requests}
                onBack={closeApplication}
                onIssueKey={() => setModal('key')}
                onAddRoute={(purposeKey = '') => { setRoutePurposeKey(purposeKey); setModal('route'); }}
                onSetQuota={() => setModal('quota')}
                onRevokeKey={(keyId) => execute(() => adminRequest(`/applications/${activeApp.id}/keys/${keyId}/revoke`, { method: 'POST' }), 'API key revoked.', false)}
                onRollKey={(keyId) => handleRollKey(activeApp.id, keyId)}
                onDeleteKey={handleDeleteKey}
                copiedId={copiedId}
                onCopy={copyToClipboard}
                onToggleStatus={handleToggleAppStatus}
                onDeleteApp={handleDeleteApp}
                onToggleRoute={handleToggleRoute}
                onDeleteRoute={handleDeleteRoute}
              />
            ) : (
              <ApplicationRegistry
                applications={applications}
                overview={overview}
                onOpenApp={openApplication}
                onNewApp={() => setModal('application')}
                copiedId={copiedId}
                onCopy={copyToClipboard}
                onToggleStatus={handleToggleAppStatus}
                onDeleteApp={handleDeleteApp}
              />
            )
          )}

          {section === 'models' && <>
            <SectionTitle eyebrow="Verified catalog" title="Models, capability, and unit economics." detail="Unknown or unpriced models fail closed before provider inference begins." action={<button className="button button--primary" onClick={() => setModal('model')}><Plus size={17} />Add model</button>} />
            <section className="panel model-catalog">{models.length ? <div className="table-wrap"><table><thead><tr><th>Model</th><th>Capabilities</th><th>Input / 1M</th><th>Output / 1M</th><th>Context</th><th>Status</th></tr></thead><tbody>{models.map((model) => <tr key={model.id}><td><span className="model-name"><ProviderBadge provider={model.providerKey} modelKey={model.modelKey} size={18} badgeSize={32} /><span><strong>{model.displayName}</strong><small>{model.providerKey} / {model.modelKey}</small></span></span></td><td><div className="chips">{model.capabilities.map((capability) => <span key={capability}>{capability.replace('_', ' ')}</span>)}</div></td><td>${Number(model.pricingRates.inputPerMillionTokens || 0).toFixed(2)}</td><td>${Number(model.pricingRates.outputPerMillionTokens || 0).toFixed(2)}</td><td>{compact(model.maxInputTokens)}</td><td><StatusPill status={model.status} /></td></tr>)}</tbody></table></div> : <EmptyState icon={<Bot />} title="Model catalog is empty" detail="Add a verified provider model and exact token pricing before configuring routes." />}</section>
          </>}

          {section === 'requests' && <>
            <SectionTitle eyebrow="Privacy-safe ledger" title="Every call, attributable and auditable." detail="Prompt text, image bytes, outputs, and secrets are intentionally excluded from this view." />
            <section className="panel request-ledger"><div className="ledger-tools"><label><Search size={17} /><input value={requestSearch} onChange={(event) => setRequestSearch(event.target.value)} placeholder="Search request, tenant, subject, feature..." /></label><span>{visibleRequests.length} records</span></div>{visibleRequests.length ? <div className="table-wrap"><table><thead><tr><th>Request</th><th>Application boundary</th><th>Feature</th><th>Model</th><th>Input</th><th>Latency</th><th>Status</th></tr></thead><tbody>{visibleRequests.map((request) => <tr key={request.id}><td><strong className="request-id">{request.id.slice(0, 8)}</strong><small>{dateTime(request.createdAt)}</small></td><td><strong>{applications.find((appItem) => appItem.id === request.appId)?.name || request.appId.slice(0, 8)}</strong><small>{request.tenantId} / {request.subjectId}</small></td><td><code>{request.feature}</code></td><td><div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}><ProviderBadge provider={request.provider} modelKey={request.model} size={13} badgeSize={22} /><span>{request.provider}<small style={{ display: 'block' }}>{request.model}</small></span></div></td><td><span className="input-flags">{request.hasFiles ? <FileImage size={16} /> : 'Text'}{request.hasResponseSchema ? <span>JSON</span> : null}</span></td><td>{request.latencyMs === null ? '—' : `${compact(request.latencyMs)} ms`}</td><td><StatusPill status={request.status} />{request.errorCode && <small className="error-code">{request.errorCode}</small>}</td></tr>)}</tbody></table></div> : <EmptyState icon={<Activity />} title="No matching requests" detail="Adjust the application filter or wait for API traffic." />}</section>
          </>}
          {section === 'operations' && <Operations applications={applications} filterApp={filterApp} onError={handleError} />}
        </div>
      </main>

      {modal === 'application' && <ModalShell title="Register application" detail="Creates a new hard isolation boundary. A credential is issued separately." onClose={() => { setModal(null); setApplicationError(''); }}><form className="form-grid" onSubmit={async (event) => { event.preventDefault(); const data = new FormData(event.currentTarget); setApplicationError(''); setBusy(true); try { await adminRequest('/applications', { method: 'POST', body: JSON.stringify({ name: data.get('name'), slug: data.get('slug'), creditsPerUsd: Number(data.get('credits')), businessMarginFactor: Number(data.get('margin')), minimumRequestCredits: Number(data.get('minimum')) }) }); setModal(null); setNoticeError(false); setNotice('Application registered.'); showToast({ type: 'success', title: 'Application Created', message: 'Application registered successfully.' }); await refresh(); } catch (error) { if (error instanceof AdminApiError && error.code === 'APP_SLUG_EXISTS') { setApplicationError('This URL-safe slug is already in use. Choose a different slug, for example agromitra-dev-v2.'); } else { handleError(error); } } finally { setBusy(false); } }}><label className="span-2">Display name<input name="name" required minLength={2} placeholder="Acme Learning" /></label><label className="span-2">URL-safe slug<input name="slug" required pattern="[a-z0-9]+(?:-[a-z0-9]+)*" aria-invalid={Boolean(applicationError)} aria-describedby={applicationError ? 'application-slug-error' : undefined} placeholder="acme-learning" />{applicationError && <span id="application-slug-error" className="form-error" role="alert"><TriangleAlert size={14} />{applicationError}</span>}</label><label>Credits per USD<input name="credits" type="number" min="0.01" step="0.01" defaultValue="100" required /></label><label>Margin factor<input name="margin" type="number" min="1" step="0.1" defaultValue="1" required /></label><label className="span-2">Minimum request credits<input name="minimum" type="number" min="0" step="1" defaultValue="1" required /></label><button className="button button--primary span-2" disabled={busy}>{busy ? 'Creating...' : 'Create application'}</button></form></ModalShell>}

      {modal === 'model' && (
        <ModalShell
          title="Add provider model"
          detail="Only allowlisted HTTPS hosts are accepted. Pricing is stored as a historical snapshot on every request."
          onClose={() => setModal(null)}
        >
          <ModelModal
            onClose={() => setModal(null)}
            onSuccess={(message) => {
              setNotice(message);
              setNoticeError(false);
              showToast({
                type: 'success',
                title: 'Model Added',
                message,
              });
              refresh();
            }}
            onError={handleError}
          />
        </ModalShell>
      )}

      {modal === 'key' && selected && <ModalShell title="Issue application key" detail="Store the credential in your application's trusted backend. Authorized administrators can copy or roll it anytime." onClose={() => { setModal(null); setIssuedKey(''); setCopiedId((curr) => (curr === 'modal-credential' ? null : curr)); }}>{issuedKey ? <div className="issued-key"><ShieldCheck size={28} /><strong>Credential issued</strong><p>Copy your key now. It is encrypted at rest using AES-GCM and available in your application dashboard.</p><code className={classNames(copiedId === 'modal-credential' && 'code--copied')} onClick={() => copyToClipboard(issuedKey, 'modal-credential')} title="Click to copy credential" role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); copyToClipboard(issuedKey, 'modal-credential'); } }}>{issuedKey}</code><button type="button" className={classNames('button button--primary button--copy', copiedId === 'modal-credential' && 'button--copied')} onClick={() => copyToClipboard(issuedKey, 'modal-credential')} aria-label={copiedId === 'modal-credential' ? 'Credential copied to clipboard' : 'Copy credential'}>{copiedId === 'modal-credential' ? <><Check size={16} className="copy-icon-check" /><span>Copied to clipboard!</span></> : <><Copy size={16} className="copy-icon" /><span>Copy credential</span></>}</button></div> : <form className="form-grid" onSubmit={async (event) => { event.preventDefault(); const data = new FormData(event.currentTarget); setBusy(true); try { const result = await adminRequest<{ apiKey: string }>(`/applications/${selected.id}/keys`, { method: 'POST', body: JSON.stringify({ name: data.get('name'), scopes: data.getAll('scopes') }) }); setIssuedKey(result.apiKey); setCopiedId((curr) => (curr === 'modal-credential' ? null : curr)); showToast({ type: 'success', title: 'Credential Issued', message: 'Application API key generated successfully.' }); await refresh(); } catch (error) { handleError(error); } finally { setBusy(false); } }}><label className="span-2">Credential name<input name="name" required placeholder="Production backend" /></label><fieldset className="span-2"><legend>Allowed operations</legend>{ALL_SCOPES.map((scope) => <label className="check" key={scope}><input type="checkbox" name="scopes" value={scope} defaultChecked={scope !== 'purposes:sync'} />{scope}</label>)}</fieldset><button className="button button--primary span-2" disabled={busy}>{busy ? 'Issuing...' : 'Issue credential'}</button></form>}</ModalShell>}

      {modal === 'route' && selected && (
        <RouteModal
          purposeKey={routePurposeKey}
          purposes={configuration.purposes}
          models={models}
          busy={busy}
          onClose={() => { setModal(null); setRoutePurposeKey(''); }}
          onSubmit={(input) => execute(
            () => adminRequest(`/applications/${selected.id}/routes`, { method: 'POST', body: JSON.stringify(input) }),
            'Purpose route saved.',
          )}
        />
      )}

      {modal === 'quota' && selected && <ModalShell title="Set monthly quota" detail="Policies are cumulative: app, tenant, and subject limits are checked before each provider call." onClose={() => setModal(null)}><form className="form-grid" onSubmit={(event) => { event.preventDefault(); const data = new FormData(event.currentTarget); const optionalNumber = (name: string) => data.get(name) ? Number(data.get(name)) : null; execute(() => adminRequest(`/applications/${selected.id}/quotas`, { method: 'POST', body: JSON.stringify({ scopeType: data.get('scopeType'), scopeId: data.get('scopeId') || '', monthlyRequests: optionalNumber('requests'), monthlyTokens: optionalNumber('tokens'), monthlyProviderCostUsdMicro: optionalNumber('cost'), monthlyCredits: optionalNumber('credits'), enabled: true }) }), 'Quota policy saved.'); }}><label>Scope<select name="scopeType" defaultValue="app" required><option value="app">Entire application</option><option value="tenant">Specific tenant</option><option value="subject">Specific end user</option></select></label><label>Scope identifier<input name="scopeId" placeholder="Empty for app scope" /></label><label>Monthly requests<input name="requests" type="number" min="1" placeholder="Unlimited" /></label><label>Monthly tokens<input name="tokens" type="number" min="1" placeholder="Unlimited" /></label><label>Provider cost (USD micro)<input name="cost" type="number" min="1" placeholder="Unlimited" /></label><label>Customer credits<input name="credits" type="number" min="1" placeholder="Unlimited" /></label><button className="button button--primary span-2" disabled={busy}>{busy ? 'Saving...' : 'Apply quota policy'}</button></form></ModalShell>}

      {deleteAppTarget && (
        <ModalShell
          title={`Delete ${deleteAppTarget.name}?`}
          detail="This action is permanent and cannot be undone."
          onClose={() => {
            setDeleteAppTarget(null);
            setDeleteAppConfirmSlug('');
            setCopiedId((curr) => (curr === 'delete-app-slug' ? null : curr));
          }}
        >
          <div className="danger-zone-modal">
            <div className="danger-zone-warning">
              <AlertTriangle size={24} className="danger-zone-icon" />
              <div>
                <strong>Warning: Cascading boundary deletion</strong>
                <p>
                  Deleting this application boundary will permanently purge all associated API keys, model routes, quota policies, job event history, logged inference records, and stored files in R2.
                </p>
              </div>
            </div>

            <div className="danger-confirm-group">
              <div className="danger-confirm-header">
                <label htmlFor="confirm-slug-input">
                  Please type{' '}
                  <code
                    className={classNames(
                      'danger-confirm-slug-code',
                      copiedId === 'delete-app-slug' && 'code--copied'
                    )}
                    onClick={(e) => {
                      e.preventDefault();
                      copyToClipboard(deleteAppTarget.slug, 'delete-app-slug');
                      const input = document.getElementById('confirm-slug-input') as HTMLInputElement | null;
                      input?.focus();
                    }}
                    title="Click to copy slug"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        copyToClipboard(deleteAppTarget.slug, 'delete-app-slug');
                        const input = document.getElementById('confirm-slug-input') as HTMLInputElement | null;
                        input?.focus();
                      }
                    }}
                  >
                    {deleteAppTarget.slug}
                  </code>{' '}
                  to confirm deletion:
                </label>
                <button
                  type="button"
                  className={classNames('copy-btn', copiedId === 'delete-app-slug' && 'copy-btn--active')}
                  onClick={() => {
                    copyToClipboard(deleteAppTarget.slug, 'delete-app-slug');
                    const input = document.getElementById('confirm-slug-input') as HTMLInputElement | null;
                    input?.focus();
                  }}
                  title={copiedId === 'delete-app-slug' ? 'Copied slug to clipboard!' : 'Copy slug to clipboard'}
                  aria-label="Copy slug to clipboard"
                >
                  {copiedId === 'delete-app-slug' ? <Check size={11} /> : <Copy size={11} />}
                  <span>{copiedId === 'delete-app-slug' ? 'Copied' : 'Copy'}</span>
                </button>
              </div>
              <input
                id="confirm-slug-input"
                type="text"
                value={deleteAppConfirmSlug}
                onChange={(e) => setDeleteAppConfirmSlug(e.target.value)}
                placeholder={deleteAppTarget.slug}
                autoFocus
              />
            </div>

            <div className="danger-modal-actions">
              <button
                type="button"
                className="button button--subtle"
                onClick={() => {
                  setDeleteAppTarget(null);
                  setDeleteAppConfirmSlug('');
                  setCopiedId((curr) => (curr === 'delete-app-slug' ? null : curr));
                }}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button button--danger"
                disabled={deleteAppConfirmSlug !== deleteAppTarget.slug || busy}
                onClick={confirmDeleteApplication}
              >
                {busy ? 'Deleting...' : 'Permanently delete application'}
              </button>
            </div>
          </div>
        </ModalShell>
      )}

      {deleteRouteTarget && (
        <ModalShell
          title={`Delete route for ${deleteRouteTarget.feature}?`}
          detail="Inference requests for this feature key will fail closed unless reconfigured."
          onClose={() => setDeleteRouteTarget(null)}
        >
          <div className="danger-zone-modal">
            <div className="danger-zone-warning">
              <AlertTriangle size={24} className="danger-zone-icon" />
              <div>
                <strong>Confirm route deletion</strong>
                <p>
                  Are you sure you want to delete the model route for <code>{deleteRouteTarget.feature}</code>? Callers requesting this feature will receive a 404 Route Not Found error.
                </p>
              </div>
            </div>

            <div className="danger-modal-actions">
              <button
                type="button"
                className="button button--subtle"
                onClick={() => setDeleteRouteTarget(null)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button button--danger"
                disabled={busy}
                onClick={confirmDeleteRoute}
              >
                {busy ? 'Deleting...' : 'Delete model route'}
              </button>
            </div>
          </div>
        </ModalShell>
      )}

      {deleteKeyTarget && (
        <ModalShell
          title={`Delete revoked key ${deleteKeyTarget.name}?`}
          detail="This record will be permanently purged from the database."
          onClose={() => setDeleteKeyTarget(null)}
        >
          <div className="danger-zone-modal">
            <div className="danger-zone-warning">
              <AlertTriangle size={24} className="danger-zone-icon" />
              <div>
                <strong>Confirm permanent key deletion</strong>
                <p>
                  Are you sure you want to permanently delete revoked API key <strong>{deleteKeyTarget.name}</strong> (<code>aip_{deleteKeyTarget.prefix}...</code>)? This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="danger-modal-actions">
              <button
                type="button"
                className="button button--subtle"
                onClick={() => setDeleteKeyTarget(null)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button button--danger"
                disabled={busy}
                onClick={confirmDeleteKey}
              >
                {busy ? 'Deleting...' : 'Permanently delete key'}
              </button>
            </div>
          </div>
        </ModalShell>
      )}
    </div>
  );
}
